<template>
  <div
    v-if="(isValidShapeId && isGrabMode) || currentShapeId"
    class="action-button-group"
    :class="{ 'is-brush': isBrush, 'no-hover': isApp(), 'is-minimized': isMinimized }"
    @click.stop
  >
    <button
      class="minimize-toggle"
      :title="isMinimized ? '펼치기' : '숨기기'"
      @click.stop="toggleMinimize"
    >
      <AppUIIconMaximize v-if="isMinimized" :size="18" color="#fff" />
      <AppUIIconMinimize v-else :size="14" color="#fff" />
    </button>
    <template v-if="!isMinimized">
      <button v-if="showShapeTypeMenu" class="shape-type-button">
        <label
          class="flex items-center justify-center gap-[4px] cursor-pointer"
          @click.stop="toggleShapeTypeMenu"
          ><p>{{ getNowShapeType() }}</p>
          <AppUIIconArrowDown size="18" stroke="--White"
        /></label>
        <div
          v-if="showedShapeTypeMenu"
          class="sub-button-group"
          :class="{ 'position-top': menuPositions.shapeType === 'top' }"
        >
          <button
            v-for="item in shapeTypeItems"
            :key="item.id"
            class="sub-button"
            :class="{ 'can-hover': !isApp() }"
            @click.stop="handleShapeTypeClick(item.id)"
          >
            <div class="anchor-point">
              <AppUIIconCheckOnly v-if="currentShapeType === item.id" size="14" color="#fff" />
            </div>
            <p>{{ item.text }}</p>
          </button>
        </div>
      </button>
      <button
        v-for="button in currentButtons"
        :key="button.id"
        class="action-button"
        :class="{
          'is-color-button': button.id === 'color',
          disabled: getDisabledButton(button.id),
        }"
        :data-action="button.id"
        @click.stop="handleButtonClick(button.id)"
      >
        <div class="button-icon">
          <AppUIIconCopy v-if="button.id === 'copy'" :size="18" color="#fff" />
          <AppUIIconPlus v-if="button.id === 'attach'" :size="13" stroke="--White" />
          <AppUIIconDeleteSimple v-if="button.id === 'delete'" :size="18" color="#fff" />
          <AppUIIconColorDot
            v-if="button.id === 'color'"
            :size="18"
            :color="getNewShape()?.color ?? shapeColorItems.find(row => row.name === 'red')?.color"
          />
          <AppUIIconAreaFill
            v-if="button.id === 'fill' && getCurrentFillState()"
            :size="18"
            color="#fff"
          />
          <AppUIIconAreaEmpty
            v-else-if="button.id === 'fill' && !getCurrentFillState()"
            :size="18"
            color="#fff"
          />
          <AppUIIconRectDash
            v-if="button.id === 'shape' && getCurrentShapeState() === 'dash'"
            :size="18"
            color="#fff"
          />
          <AppUIIconRectCloud
            v-else-if="button.id === 'shape' && getCurrentShapeState() === 'cloud'"
            :size="18"
            color="#fff"
          />
          <AppUIIconAreaEmpty
            v-else-if="button.id === 'shape' && getCurrentShapeState() === 'line'"
            :size="18"
            color="#fff"
          />
          <AppUIIconAlignLeft v-else-if="button.id === 'align'" :size="18" color="#fff" />
          <AppUIIconGroup v-if="button.id === 'combine'" :size="18" color="#fff" />
          <AppUIIconUnGroup v-if="button.id === 'ungroup'" :size="18" color="#fff" />
          <AppUIIconArrow
            v-if="button.id === 'tripodTail' && getCurrentTailStyle() === 'arrow'"
            :size="18"
            color="#fff"
          />
          <AppUIIconDot
            v-else-if="button.id === 'tripodTail' && getCurrentTailStyle() === 'dot'"
            :size="18"
            color="#fff"
          />
          <AppUIIconNumbering v-if="button.id === 'order'" :size="18" color="#fff" />
          <AppUIIconChange
            v-if="button.id === 'becomeCurrent' || button.id === 'becomePrevious'"
            :size="18"
            color="#fff"
          />
        </div>
        <span class="button-text">{{ button.text }}</span>

        <!-- 색상 팔레트 -->
        <div
          v-if="button.id === 'color' && showColorPalette"
          class="color-palette"
          :class="{ 'position-top': menuPositions.color === 'top' }"
          @click.stop
        >
          <div class="color-options">
            <p>테두리</p>
            <button
              v-for="color in shapeColorItems.filter(item => item.name !== 'purple')"
              :key="color.name"
              class="color-option"
              :class="{ active: currentColor === color.color }"
              :style="{ backgroundColor: color.color }"
              :title="color.name"
              @click.stop="colorHandler(color.color, 'border')"
            ></button>
          </div>
          <div class="color-options outline-options">
            <p>채우기</p>
            <button
              v-for="color in shapeColorItems.filter(item => item.name !== 'purple')"
              :key="color.name"
              class="color-option"
              :class="{ active: currentFillColor === color.color }"
              :style="{ backgroundColor: color.color }"
              :title="color.name"
              @click.stop="colorHandler(color.color, 'fill')"
            >
              <!-- white 채우기이고 투명일 때 대각선 슬래시 -->
              <div
                v-if="color.name === 'none' && getBalloonFillOpacity() === 0"
                class="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div class="w-[20px] h-[1.5px] bg-red-500 transform rotate-45"></div>
              </div>
            </button>
          </div>
          <div class="color-options outline-options">
            <p>숫자</p>
            <button
              v-for="color in shapeColorItems.filter(item => item.name !== 'purple')"
              :key="color.name"
              class="color-option"
              :class="{ active: currentNumberColor === color.color }"
              :style="{ backgroundColor: color.color }"
              :title="color.name"
              @click.stop="colorHandler(color.color, 'number')"
            ></button>
          </div>
        </div>

        <!-- 정렬 옵션 (다중선택) -->
        <div
          v-if="button.id === 'align' && showAlignOptions"
          class="color-palette"
          :class="{ 'position-top': menuPositions.align === 'top' }"
          @click.stop
        >
          <div class="color-options">
            <p>번호풍선</p>
            <button
              v-for="option in ALIGN_OPTIONS"
              :key="option.id"
              class="align-option"
              @click.stop="handleGrabAlign(option.id, 'balloon')"
            >
              <component :is="option.component" :size="16" color="#fff" />
            </button>
          </div>
          <div v-if="checkShowDirectionAlignOptions()" class="color-options outline-options">
            <p>꺾은선</p>
            <button
              v-for="option in ALIGN_OPTIONS"
              :key="`direction-${option.id}`"
              class="align-option"
              :class="{ disabled: option.id === 'horizontal' || option.id === 'vertical' }"
              @click.stop="
                option.id !== 'horizontal' && option.id !== 'vertical'
                  ? handleGrabAlign(option.id, 'direction')
                  : null
              "
            >
              <component
                :is="option.component"
                :size="16"
                :color="option.id === 'horizontal' || option.id === 'vertical' ? '#888' : '#fff'"
              />
            </button>
          </div>
        </div>

        <!-- 삭제 옵션 리스트 -->
        <div
          v-if="button.id === 'delete' && showDeleteOptions"
          class="delete-options"
          :class="{ 'position-top': menuPositions.delete === 'top' }"
          @click.stop
        >
          <div class="delete-option-list">
            <button class="delete-option" @click.stop="handleDeleteOption(true)">전체 삭제</button>
            <button
              v-for="shape in relatedShapes.filter(s => !s.parent_defect_id)"
              :key="shape.id"
              class="delete-option"
              @click.stop="handleDeleteOption(false, shape.id.toString())"
            >
              {{
                `${currentShapeIndex}-${relatedShapes.findIndex(s => s.id === shape.id) + 1}만 삭제`
              }}
            </button>
          </div>
        </div>

        <!-- 번호 이동 옵션 -->
        <div
          v-if="button.id === 'order' && showOrderOptions"
          class="delete-options"
          :class="{ 'position-top': menuPositions.order === 'top' }"
          @click.stop
        >
          <div class="delete-option-list">
            <button class="delete-option" @click.stop="handleOrderChange('up')">번호 위로</button>
            <button class="delete-option" @click.stop="handleOrderChange('down')">
              번호 아래로
            </button>
          </div>
        </div>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
  import {
    BUTTON_DEFINITIONS,
    SHAPE_BUTTON_CONFIG,
    getCurrentColorName,
    shapeColorItems,
    shapeTypeItems,
    type ButtonDefType,
  } from '@/app/constant'
  import { resolveSafeDisplayColors } from '@/app/function/defectMode'
  import { isApp } from '@/index'
  import { useShapeStore } from '@/app/data/Editor/shape'
  import { useWebEditorDataStore, useWebEditorFacilityStore } from '@/app/store/webeditor'
  import { useUndoRedo } from '@/app/undoRedo'
  import { storeToRefs } from 'pinia'
  import { Defect } from '@/app/data/types'
  import { cloneDeep } from 'lodash'
  import { App } from '@/app'
  import { useDefectPhotoStore } from '@/app/data/Editor/defectPhoto'
  import { useCanvasStore } from '@/app/store/canvas'
  import { getRelatedShapes } from '@/app/function'
  import { useAllTaskStore } from '@/app/store/task'
  import { CanvasEngineEntity, SHAPES } from '@/lib/CanvasEngine'
  import { snapToDirections } from '@/lib/CanvasEngine/entity/types/direction/stateManager'
  import {
    alignBalloonsInOrder,
    calculateAlignTargetValue,
    type BalloonData,
    type AlignDirection,
    findDirectionPathToBalloon,
    alignDirectionShapes,
  } from '@/lib/CanvasEngine/helper/align'

  // 정렬 아이콘 컴포넌트들
  import AppUIIconAlignHorizontal from '@/components/AppUI/Icon/Align/Horizontal.vue'
  import AppUIIconAlignLeft from '@/components/AppUI/Icon/Align/Left.vue'
  import AppUIIconAlignRight from '@/components/AppUI/Icon/Align/Right.vue'
  import AppUIIconAlignBottom from '@/components/AppUI/Icon/Align/Bottom.vue'
  import AppUIIconAlignVertical from '@/components/AppUI/Icon/Align/Vertical.vue'
  import AppUIIconAlignTop from '@/components/AppUI/Icon/Align/Top.vue'
  import AppUIIconMinimize from '@/components/AppUI/Icon/Minimize.vue'
  import AppUIIconMaximize from '@/components/AppUI/Icon/Maximize.vue'
  import { useAppEditorFacilityStore } from '@/app/store/editor'
  import {
    isPreviousShape as isPreviousShapeUtil,
    generateKeyBetween,
    sortByKeys,
  } from '@/app/util'
  import { useUserStore } from '@/app/store/user'
  const toast = App.toastInstance
  const userStore = useUserStore()
  interface Props {
    isBrush?: boolean
    onCopy?: (shapeId: string) => void
    onAttach?: (shapeId: string) => void
    onDelete?: (shapeIds: string[]) => void
    onCombine?: (shapeIds: string[]) => void
    onUngroup?: (shapeId: string) => void
    onTripodTail?: (shapeId: string) => void
  }

  const canvasStore = useCanvasStore()
  const app_shapeStore = useShapeStore()
  const app_defectPhotoStore = useDefectPhotoStore()
  const allTaskStore = useAllTaskStore()

  const web_dataStore = useWebEditorDataStore()

  const { GetItem } = storeToRefs(app_shapeStore)
  const { selectedShapeId } = storeToRefs(web_dataStore)
  const { canvasSelectedShapeId, grabbedEntityIds, isGrabMode, grabBoxRect } =
    storeToRefs(canvasStore)
  const { isPrevRegistMode } = storeToRefs(allTaskStore)

  const props = withDefaults(defineProps<Props>(), {
    isBrush: false,
    onCopy: () => {},
    onDelete: () => {},
    onAttach: () => {},
    onCombine: () => {},
    onUngroup: () => {},
    onTripodTail: () => {},
  })

  function getDisabledButton(buttonId: string): boolean {
    const targetButtons = ['color', 'fill', 'shape']
    if (targetButtons.includes(buttonId)) {
      return !userStore.checkEnterprise()
    }
    return false
  }

  const shapeType = computed(() => {
    return shapeData.value?.shape_type
  })

  const showShapeTypeMenu = computed(() => {
    if (isPreviousShape.value && !isPreviousHasDefectPhoto.value) return false
    return (
      shapeType.value === SHAPES.POINT ||
      shapeType.value === SHAPES.DIRECTION ||
      shapeType.value === SHAPES.RECT
    )
  })

  // 현재 선택된 모든 도형 id들
  const currentShapeIds = computed<string[]>(() => {
    // grabBox가 활성화되고 그랩된 도형이 있으면 모든 도형 반환
    if (isGrabMode.value && grabbedEntityIds.value.length > 0) return grabbedEntityIds.value
    return []
  })

  // 현재 선택된 도형 id
  const currentShapeId = computed<string | undefined>(() => {
    return isApp() ? canvasSelectedShapeId.value : selectedShapeId.value
  })

  // 다중 선택 여부
  const isMultipleSelection = computed<boolean>(() => {
    return !currentShapeId.value && currentShapeIds.value.length > 0
  })

  const multiplePointShapeIds = computed(() => {
    return currentShapeIds.value.filter(id => canvasStore.getEntityById(id)?.isPoint())
  })

  // 색상 팔레트 표시 상태
  const showColorPalette = ref<boolean>(false)

  // 도형 타입 메뉴 표시 상태
  const showedShapeTypeMenu = ref<boolean>(false)

  // 정렬 옵션 표시 상태
  const showAlignOptions = ref<boolean>(false)

  // 삭제 옵션 표시 상태
  const showDeleteOptions = ref<boolean>(false)

  // 번호 이동 옵션 표시 상태
  const showOrderOptions = ref<boolean>(false)

  // 최소화 상태 (localStorage에 영속)
  const MINIMIZED_STORAGE_KEY = 'actionButtonGroup_minimized'
  const isMinimized = ref<boolean>(localStorage.getItem(MINIMIZED_STORAGE_KEY) === '1')

  // 최소화/펼치기 토글
  function toggleMinimize(): void {
    isMinimized.value = !isMinimized.value
    localStorage.setItem(MINIMIZED_STORAGE_KEY, isMinimized.value ? '1' : '0')
    // 모든 서브 메뉴 닫기
    showColorPalette.value = false
    showAlignOptions.value = false
    showDeleteOptions.value = false
    showOrderOptions.value = false
    showedShapeTypeMenu.value = false
  }

  // 메뉴 위치 상태들
  const menuPositions = ref({
    shapeType: 'bottom' as 'top' | 'bottom',
    color: 'bottom' as 'top' | 'bottom',
    align: 'bottom' as 'top' | 'bottom',
    delete: 'bottom' as 'top' | 'bottom',
    order: 'bottom' as 'top' | 'bottom',
  })

  // 정렬 옵션 상수 정의
  const ALIGN_OPTIONS: readonly {
    id: 'left' | 'horizontal' | 'right' | 'top' | 'vertical' | 'bottom'
    component: unknown
  }[] = [
    { id: 'left', component: AppUIIconAlignLeft },
    { id: 'horizontal', component: AppUIIconAlignHorizontal },
    { id: 'right', component: AppUIIconAlignRight },
    { id: 'top', component: AppUIIconAlignTop },
    { id: 'vertical', component: AppUIIconAlignVertical },
    { id: 'bottom', component: AppUIIconAlignBottom },
  ] as const

  /**
   * 뷰포트 공간을 체크하여 메뉴 위치를 결정하는 함수
   * @param {HTMLElement} triggerElement - 트리거 요소
   * @param {number} menuHeight - 메뉴 예상 높이
   * @returns {'top' | 'bottom'} 메뉴 위치
   */
  function calculateMenuPosition(
    triggerElement: HTMLElement,
    menuHeight: number,
  ): 'top' | 'bottom' {
    const triggerRect = triggerElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight

    // 아래쪽 여유 공간
    const spaceBelow = viewportHeight - triggerRect.bottom

    // 아래쪽에 충분한 공간이 있으면 아래로, 없으면 위로
    return spaceBelow >= menuHeight ? 'bottom' : 'top'
  }

  /**
   * 메뉴 위치를 업데이트하는 함수
   * @param {string} menuType - 메뉴 타입
   * @param {HTMLElement} triggerElement - 트리거 요소
   * @param {number} menuHeight - 메뉴 예상 높이
   */
  function updateMenuPosition(
    menuType: keyof typeof menuPositions.value,
    triggerElement: HTMLElement,
    menuHeight: number,
  ): void {
    nextTick(() => {
      menuPositions.value[menuType] = calculateMenuPosition(triggerElement, menuHeight)
    })
  }

  // shapeId 변경 감지하여 메뉴 상태 초기화
  watch(
    () => currentShapeId,
    (newShapeId, oldShapeId) => {
      // shapeId가 바뀌거나 없어지면 모든 메뉴 상태 초기화
      if (newShapeId !== oldShapeId) {
        showColorPalette.value = false
        showAlignOptions.value = false
        showDeleteOptions.value = false
        showOrderOptions.value = false
      }
    },
    { immediate: true },
  )

  // 선택된 도형이 유효하지 않은 경우 컴포넌트 숨김
  const isValidShapeId = computed(() => {
    // 선택된 도형이 하나라도 있으면 표시
    return currentShapeIds.value.length > 0
  })

  const currentShapeData = computed(() => {
    if (!currentShapeId.value) return null
    if (isApp()) {
      return GetItem.value(currentShapeId.value)
    } else {
      return web_dataStore.getShape(currentShapeId.value)
    }
  })

  // 관련된 shape들 계산
  const relatedShapes = computed(() => {
    if (!currentShapeData.value || !currentShapeId.value) return []
    try {
      if (isApp()) {
        // 앱 환경에서는 getRelatedShapesToDelete 함수 사용
        const related = app_shapeStore.getCurrentRelatedShapes(currentShapeId.value.toString())
        return related || []
      } else {
        const floorplanId = currentShapeData.value.floorplan_id
        if (floorplanId) {
          const shapes = web_dataStore.getShapes(floorplanId)
          const related = getRelatedShapes(currentShapeId.value, shapes)
          return related || []
        }
        return []
      }
    } catch (error) {
      console.warn('relatedShapes 계산 중 오류:', error)
      return []
    }
  })

  // 현재 shape 인덱스 계산
  const currentShapeIndex = computed(() => {
    // 캔버스 번호 재부여(getIndex는 비반응형) 시 재계산되도록 버전 카운터 구독
    void canvasStore.indexVersion
    if (!currentShapeId.value) return ''
    let targetId = currentShapeId.value
    // 연관 목록이 있으면 부모기준으로 index 구함
    if (relatedShapes.value.length > 0) {
      const parentShape = relatedShapes.value[0]
      // 앱에서는 localStringId 사용, 웹에서는 id 사용
      targetId = isApp()
        ? (parentShape as Defect).localStringId || parentShape.id.toString()
        : parentShape.id.toString()
    }
    let index = canvasStore.getIndex(targetId)
    // index가 undefined이거나 '-1'인 경우 현재 shapeId로 다시 시도
    if (!index || index === '-1') {
      index = canvasStore.getIndex(currentShapeId.value)
    }

    // 여전히 인덱스를 찾을 수 없는 경우 기본값 반환
    return index || ''
  })

  // 자식 요소가 있는지 확인
  const hasChildShapes = computed(() => {
    return relatedShapes.value.length > 1
  })

  // 인덱스 재계산에 영향을 주는 필드만 추적 — 무관 필드 변경 시 전량 리드로우(updateIndex) 방지
  const relatedShapesIndexKey = computed(() =>
    relatedShapes.value
      .map(
        s =>
          `${(s as Defect).localStringId ?? s.id}:${s.parent_defect_id ?? ''}:${s.previous_balloon_number ?? ''}`,
      )
      .join('|'),
  )

  // relatedShapes 변경 감지하여 인덱스 업데이트
  watch(relatedShapesIndexKey, () => {
    // relatedShapes가 변경되면 다음 틱에서 인덱스 재계산
    nextTick(() => {
      if (currentShapeId.value && relatedShapes.value.length > 0) {
        canvasStore.updateIndex()
      }
    })
  })

  const isPreviousShape = computed(() => {
    if (!currentShapeData.value) return false
    return isPreviousShapeUtil(currentShapeData.value.previous_defect_id)
  })

  const isPreviousHasDefectPhoto = computed(() => {
    if (!shapeData.value || !currentShapeId.value) return false
    if (isApp()) {
      return app_defectPhotoStore.getIsPreviousHasDefectPhoto(currentShapeId.value)
    } else {
      return web_dataStore.getIsPreviousHasDefectPhoto(currentShapeId.value)
    }
  })

  const shapeData = ref<Defect | null>(null)
  watch(
    currentShapeData,
    () => {
      shapeData.value = currentShapeData.value as Defect | null
    },
    { immediate: true },
  )

  const currentShapeType = computed(() => {
    if (!shapeData.value) return ''
    const { newShape } = findParentAndReturnShape(shapeData.value)
    return newShape.shape_type
  })
  // 안전망: shape.color가 보라이지만 표시는 'previous'가 아닐 때 (잘못 저장된 데이터) 정상 색상으로
  function safeColors(newShape: Defect): {
    borderColor: string
    fontColor: string
    fillColor: string
  } | null {
    if (getCurrentColorName(newShape.color) !== 'purple') return null
    const styles = canvasStore.getCurrentCreateStyles()
    return resolveSafeDisplayColors(newShape, {
      borderColor: styles.balloon_border_color,
      fontColor: styles.balloon_font_color,
      fillColor: styles.balloon_fill_color,
    })
  }
  const currentColor = computed(() => {
    if (!shapeData.value) return null
    const { newShape } = findParentAndReturnShape(shapeData.value)
    return safeColors(newShape)?.borderColor ?? newShape.color
  })
  const currentFillColor = computed(() => {
    if (!shapeData.value) return null
    const { newShape } = findParentAndReturnShape(shapeData.value)
    return safeColors(newShape)?.fillColor ?? newShape.shape_data.balloonFillColor
  })
  const currentNumberColor = computed(() => {
    if (!shapeData.value) return null
    const { newShape } = findParentAndReturnShape(shapeData.value)
    return safeColors(newShape)?.fontColor ?? newShape.shape_data.balloonFontColor
  })

  function getNewShape(): Defect | null {
    if (shapeData.value) {
      const { newShape } = findParentAndReturnShape(shapeData.value)
      if (!newShape) return shapeData.value
      return newShape
    }
    return null
  }

  const currentButtons = computed<
    {
      id: string
      text: ButtonDefType
    }[]
  >(() => {
    if (isMultipleSelection.value) {
      // 다중 선택인 경우
      const buttons: (typeof BUTTON_DEFINITIONS)[keyof typeof BUTTON_DEFINITIONS][] = []

      // 삭제
      buttons.push(BUTTON_DEFINITIONS.delete)

      // 정렬 (PC 전용)
      if (!isApp()) buttons.unshift(BUTTON_DEFINITIONS.align)

      // 그룹 (삼발이) - PC 전용
      if (
        !isApp() &&
        currentShapeIds.value.length > 1 &&
        isMultipleSelection.value &&
        multiplePointShapeIds.value.length === currentShapeIds.value.length
      ) {
        buttons.unshift(BUTTON_DEFINITIONS.combine)
      }

      // 번호 이동 - 편집 가능한 shape가 있을 때만 표시
      const hasColorChangeableShape = currentShapeIds.value.some(shapeId => {
        const shape = isApp()
          ? GetItem.value(shapeId.toString())
          : web_dataStore.getShape(shapeId.toString())
        return checkIsPrevious(shape)
      })

      // 색상 (PC 전용)
      if (!isApp() && hasColorChangeableShape) buttons.unshift(BUTTON_DEFINITIONS.color)

      // 그룹 꼬리 — 모든 선택이 POINT 또는 TRIPOD일 때 표시 (다중)
      const allPointOrTripod = currentShapeIds.value.every(id => {
        const e = canvasStore.getEntityById(id)
        return e?.isPoint() || e?.isTripod()
      })
      if (allPointOrTripod && currentShapeIds.value.length > 0) {
        buttons.unshift(BUTTON_DEFINITIONS.tripodTail)
      }

      if (hasColorChangeableShape) {
        buttons.splice(buttons.length - 1, 0, BUTTON_DEFINITIONS.order)
      }

      // 현재차로 변경 / 전차로 변경 — 모든 선택이 전차 도형일 때 (전차등록모드 제외)
      if (!isPrevRegistMode.value) {
        const resolvedShapes = currentShapeIds.value.map(id =>
          isApp() ? GetItem.value(id) : web_dataStore.getShape(id),
        )
        const allPreviousShapes = resolvedShapes.every(
          shape => shape && isPreviousShapeUtil(shape.previous_defect_id),
        )
        if (allPreviousShapes && resolvedShapes.length > 0) {
          const allIsCurrent = resolvedShapes.every(shape => shape?.is_current)
          const allIsNotCurrent = resolvedShapes.every(shape => !shape?.is_current)
          if (allIsNotCurrent) {
            buttons.splice(buttons.length - 1, 0, BUTTON_DEFINITIONS.becomeCurrent)
          } else if (allIsCurrent) {
            buttons.splice(buttons.length - 1, 0, BUTTON_DEFINITIONS.becomePrevious)
          }
        }
      }

      return buttons
    } else {
      // 단일 선택인 경우
      if (checkIsPrevious(currentShapeData.value) || isPreviousHasDefectPhoto.value) {
        // 전차 shape이면 번호 버튼 제외
        if (isPreviousShape.value) {
          const filtered = buttons.value.filter(button => button.id !== 'order')
          // 현재차/전차 전환 버튼 추가 (전차등록모드 제외)
          if (!isPrevRegistMode.value) {
            if (currentShapeData.value?.is_current) {
              filtered.splice(filtered.length - 1, 0, BUTTON_DEFINITIONS.becomePrevious)
            } else {
              filtered.splice(filtered.length - 1, 0, BUTTON_DEFINITIONS.becomeCurrent)
            }
          }
          return filtered
        }
        return buttons.value
      }
      // 전차항목은 삭제, 꼬리, 현재차/전차 전환 버튼 표시
      const filtered = buttons.value.filter(
        button => button.id === 'delete' || button.id === 'tripodTail',
      )
      // 전차 도형인 경우에만 전환 버튼 추가 (전차등록모드 제외)
      if (isPreviousShape.value && !isPrevRegistMode.value) {
        if (currentShapeData.value?.is_current) {
          filtered.splice(filtered.length - 1, 0, BUTTON_DEFINITIONS.becomePrevious)
        } else {
          filtered.splice(filtered.length - 1, 0, BUTTON_DEFINITIONS.becomeCurrent)
        }
      }
      return filtered
    }
  })

  // 삭제 옵션 처리 함수
  function handleDeleteOption(isAll: boolean, shapeId?: string): void {
    if (isAll) {
      // 전체 삭제 - 부모 요소(0번째 요소) 삭제
      if (relatedShapes.value.length > 0) {
        const parentShape = relatedShapes.value[0]
        props.onDelete?.([parentShape.id.toString()])
      }
    } else if (!isAll && shapeId) {
      // 개별 삭제 - 해당 자식 요소만 삭제
      props.onDelete?.([shapeId])
    }
    // 삭제 옵션 닫기
    showDeleteOptions.value = false
  }

  /**
   * 색상 변경을 처리하는 함수
   * @param {string} color - 변경할 색상
   * @returns {Promise<void>}
   */
  async function colorHandler(color: string, type: 'border' | 'fill' | 'number'): Promise<void> {
    if (isMultipleSelection.value) {
      // 다중 선택
      // 전차가 아닌 결함만 필터링
      const targetIds = currentShapeIds.value
        .map(id => {
          const shape = isApp() ? app_shapeStore.GetItem(id) : web_dataStore.getShape(id)
          return shape
        })
        .filter(shape => checkIsPrevious(shape || null))
        .map(shape =>
          isApp() ? (shape as Defect).localStringId : shape!.id.toString(),
        ) as string[]
      switch (type) {
        case 'border':
          // 테두리 (레이어)
          await updateMultipleShapeValue(
            targetIds,
            shape => ({
              shape_data: {
                ...shape.shape_data,
                color: color,
                balloonBorderColor: color,
              },
              color: color,
            }),
            `${targetIds.length}개 결함 채우기 색상 변경이`,
          )

          break
        case 'fill':
          // 채우기 색상 변경
          await updateMultipleShapeValue(
            targetIds,
            shape => ({
              shape_data: {
                ...shape.shape_data,
                balloonFillColor: color,
              },
            }),
            `${targetIds.length}개 결함 채우기 색상 변경이`,
          )

          // 캔버스 엔티티들에도 변경 사항 적용
          targetIds.forEach(shapeId => {
            const entity = canvasStore.getEntityById(shapeId)
            if (entity) {
              entity.setBalloonFillColor(color)
            }
          })
          break
        case 'number':
          // 숫자 색상 변경
          await updateMultipleShapeValue(
            targetIds,
            shape => ({
              shape_data: {
                ...shape.shape_data,
                balloonFontColor: color,
              },
            }),
            `${targetIds.length}개 결함 숫자 색상 변경이`,
          )

          // 캔버스 엔티티들에도 변경 사항 적용
          targetIds.forEach(shapeId => {
            const entity = canvasStore.getEntityById(shapeId)
            if (entity) {
              entity.setBalloonFontColor(color)
            }
          })
          break
      }
    } else {
      // 단일 선택
      if (!shapeData.value) return
      const { newShape, currentId } = findParentAndReturnShape(shapeData.value)
      const newColor = color
      if (!newShape) {
        toast.add('error', '결함을 찾을 수 없습니다.')
        return
      }
      switch (type) {
        case 'border':
          newShape.color = newColor
          newShape.shape_data.color = newColor
          newShape.shape_data.isChanged = true
          updateShapeValue(newShape)
          if (currentId) {
            const entity = canvasStore.getEntityById(currentId.toString())
            if (entity) {
              entity.changeColor(newColor)
            }
          }
          // 업데이트 성공 후 현재 상태를 마지막 저장 상태로 갱신
          allTaskStore.updateCreateShapeData(newShape)
          break
        case 'fill':
          newShape.shape_data.balloonFillColor = newColor
          newShape.shape_data.isChanged = true
          updateShapeValue(newShape)

          if (currentId) {
            const entity = canvasStore.getEntityById(currentId.toString())
            if (entity) {
              entity.setBalloonFillColor(newColor)
            }
          }
          // 업데이트 성공 후 현재 상태를 마지막 저장 상태로 갱신
          allTaskStore.updateCreateShapeData(newShape)
          break
        case 'number':
          newShape.shape_data.balloonFontColor = newColor
          newShape.shape_data.isChanged = true
          updateShapeValue(newShape)

          if (currentId) {
            const entity = canvasStore.getEntityById(currentId.toString())
            if (entity) {
              entity.setBalloonFontColor(newColor)
            }
          }
          // 업데이트 성공 후 현재 상태를 마지막 저장 상태로 갱신
          allTaskStore.updateCreateShapeData(newShape)
          break
      }
    }
  }

  function getNowShapeType(): string {
    switch (currentShapeType.value) {
      case SHAPES.DIRECTION:
        return '방향'
      case SHAPES.RECT:
        return '영역'
      case SHAPES.POINT:
        return '점'
      default:
        return currentShapeType.value as SHAPES
    }
  }

  /**
   * Defect type 메뉴 토글 함수
   */
  function toggleShapeTypeMenu(): void {
    const willShow = !showedShapeTypeMenu.value
    showedShapeTypeMenu.value = willShow

    if (willShow) {
      const buttonElement = document.querySelector('.shape-type-button label') as HTMLElement
      if (buttonElement) {
        updateMenuPosition('shapeType', buttonElement, 100) // shape type 메뉴 예상 높이
      }
    }
  }
  /**
   * RECT의 중심점에서 balloon 위치로의 벡터를 8방향 중 하나로 스냅하여 새로운 balloon 위치를 계산
   * @param centerPoint RECT의 중심점 좌표
   * @param balloonAbsolutePoint balloon의 절대 좌표
   * @returns 8방향으로 스냅된 새로운 balloon 절대 좌표
   */
  function calculateEightAngle(
    centerPoint: { x: number; y: number },
    balloonAbsolutePoint: { x: number; y: number },
  ): {
    x: number
    y: number
  } {
    // 중심점에서 balloon으로의 벡터 계산
    const dx = balloonAbsolutePoint.x - centerPoint.x
    const dy = balloonAbsolutePoint.y - centerPoint.y

    // 벡터 길이 계산
    const distance = Math.sqrt(dx * dx + dy * dy)

    // 거리가 너무 짧으면 기본값 반환 (북동쪽 방향으로 50px)
    if (distance < 10) {
      return { x: centerPoint.x + 35, y: centerPoint.y - 35 }
    }

    // 8방향 스냅 함수 활용
    const snappedDirection = snapToDirections(dx, dy)

    // 스냅된 방향으로 원래 거리를 유지하여 새로운 balloon 위치 계산
    const newDistance = distance

    const newX = centerPoint.x + snappedDirection.x * newDistance
    const newY = centerPoint.y + snappedDirection.y * newDistance

    return { x: newX, y: newY }
  }

  // 공통 유틸리티 함수들
  function calculateCenterPoint(
    point: { x: number; y: number },
    width: number,
    height: number,
  ): { x: number; y: number } {
    return {
      x: point.x + width / 2,
      y: point.y + height / 2,
    }
  }

  function calculateBalloonAbsolutePoint(
    point: { x: number; y: number },
    balloonPoint: { x: number; y: number },
  ): { x: number; y: number } {
    return {
      x: point.x + balloonPoint.x,
      y: point.y + balloonPoint.y,
    }
  }

  function getDirectionFirstAndLastPoints(points: number[]): {
    first: { x: number; y: number }
    last: { x: number; y: number }
  } | null {
    if (!points || points.length < 4) {
      toast.add('error', '방향을 찾을 수 없습니다.')
      return null
    }
    return {
      first: { x: points[0], y: points[1] },
      last: { x: points[points.length - 2], y: points[points.length - 1] },
    }
  }

  // 변환 타입 정의
  type ShapeData = {
    point: { x: number; y: number }
    width: number
    height: number
    points: number[]
    balloonPoint: { x: number; y: number }
  }

  type ConvertedShapeData = {
    point: { x: number; y: number }
    width: number
    height: number
    points: number[]
    balloonPoint: { x: number; y: number }
  }

  // 각 변환 타입별 함수들
  function convertRectToDirection(originData: ShapeData): ConvertedShapeData {
    const centerPoint = calculateCenterPoint(originData.point, originData.width, originData.height)
    const balloonAbsolutePoint = calculateBalloonAbsolutePoint(
      originData.point,
      originData.balloonPoint,
    )
    const calculatedBalloonPoint = calculateEightAngle(centerPoint, balloonAbsolutePoint)

    return {
      point: { x: 0, y: 0 },
      width: 0,
      height: 0,
      points: [centerPoint.x, centerPoint.y, calculatedBalloonPoint.x, calculatedBalloonPoint.y],
      balloonPoint: calculatedBalloonPoint,
    }
  }

  function convertRectToPoint(originData: ShapeData): ConvertedShapeData {
    const centerPoint = calculateCenterPoint(originData.point, originData.width, originData.height)
    const balloonAbsolutePoint = calculateBalloonAbsolutePoint(
      originData.point,
      originData.balloonPoint,
    )

    return {
      point: centerPoint,
      width: canvasStore.getCanvasPointSize(),
      height: canvasStore.getCanvasPointSize(),
      points: [],
      balloonPoint: {
        x: balloonAbsolutePoint.x - centerPoint.x,
        y: balloonAbsolutePoint.y - centerPoint.y,
      },
    }
  }

  function convertPointToDirection(originData: ShapeData): ConvertedShapeData {
    const balloonAbsolutePoint = calculateBalloonAbsolutePoint(
      originData.point,
      originData.balloonPoint,
    )
    const calculatedBalloonPoint = calculateEightAngle(originData.point, balloonAbsolutePoint)

    return {
      point: { x: 0, y: 0 },
      width: 0,
      height: 0,
      points: [
        originData.point.x,
        originData.point.y,
        calculatedBalloonPoint.x,
        calculatedBalloonPoint.y,
      ],
      balloonPoint: calculatedBalloonPoint,
    }
  }

  function convertPointToRect(originData: ShapeData): ConvertedShapeData {
    const size = 20
    return {
      point: { x: originData.point.x - size / 2, y: originData.point.y - size / 2 },
      width: size,
      height: size,
      points: [],
      balloonPoint: originData.balloonPoint,
    }
  }

  function convertDirectionToRect(originData: ShapeData): ConvertedShapeData | null {
    const directionPoints = getDirectionFirstAndLastPoints(originData.points)
    if (!directionPoints) return null

    const size = 20
    return {
      point: { x: directionPoints.first.x - size / 2, y: directionPoints.first.y - size / 2 },
      width: size,
      height: size,
      points: [],
      balloonPoint: {
        x: directionPoints.last.x - directionPoints.first.x,
        y: directionPoints.last.y - directionPoints.first.y,
      },
    }
  }

  function convertDirectionToPoint(originData: ShapeData): ConvertedShapeData | null {
    const directionPoints = getDirectionFirstAndLastPoints(originData.points)
    if (!directionPoints) return null

    return {
      point: directionPoints.first,
      width: canvasStore.getCanvasPointSize(),
      height: canvasStore.getCanvasPointSize(),
      points: [],
      balloonPoint: {
        x: directionPoints.last.x - directionPoints.first.x,
        y: directionPoints.last.y - directionPoints.first.y,
      },
    }
  }

  async function handleShapeTypeClick(shapeTypeId: SHAPES): Promise<void> {
    if (!shapeData.value) return
    const { newShape, currentId } = findParentAndReturnShape(shapeData.value)
    const targetShapeType = newShape.shape_type

    if (targetShapeType === SHAPES.TRIPOD) return

    // 변환 함수 매핑
    const conversionMap: Record<string, (data: ShapeData) => ConvertedShapeData | null> = {
      [`${SHAPES.RECT}_${SHAPES.DIRECTION}`]: convertRectToDirection,
      [`${SHAPES.RECT}_${SHAPES.POINT}`]: convertRectToPoint,
      [`${SHAPES.POINT}_${SHAPES.DIRECTION}`]: convertPointToDirection,
      [`${SHAPES.POINT}_${SHAPES.RECT}`]: convertPointToRect,
      [`${SHAPES.DIRECTION}_${SHAPES.RECT}`]: convertDirectionToRect,
      [`${SHAPES.DIRECTION}_${SHAPES.POINT}`]: convertDirectionToPoint,
    }

    const conversionKey = `${targetShapeType}_${shapeTypeId}`
    const convertFunction = conversionMap[conversionKey]

    if (!convertFunction) {
      if (targetShapeType === shapeTypeId) return
      toast.add('error', '지원하지 않는 변환입니다.')
      return
    }

    const convertedData = convertFunction(newShape.shape_data as ShapeData)
    if (!convertedData) return

    const applyData = {
      ...newShape,
      shape_data: {
        ...newShape.shape_data,
        ...convertedData,
        type: shapeTypeId,
        // POINT 외의 타입으로 변환 시 dotType 흔적 제거
        ...(shapeTypeId !== SHAPES.POINT ? { dotType: undefined } : {}),
      },
      shape_type: shapeTypeId,
    }

    // 데이터 저장 및 캔버스 업데이트
    if (isApp()) {
      const facilityStore = useAppEditorFacilityStore()
      await app_shapeStore.updateShapeToHistory(applyData, true)
      const getAllShape = app_shapeStore.getSortedShapes(facilityStore.selectedFloorDatabaseId)
      canvasStore.reloadShape(applyData)
      canvasStore.sortEntities(getAllShape.map(shape => shape.localStringId.toString()))
      canvasStore.selectShapeById(currentId)
    } else {
      const facilityStore = useWebEditorFacilityStore()
      await web_dataStore.updateShapeToHistory(applyData, true)
      const getAllShapes = web_dataStore.getShapes(facilityStore.selectedFloorDatabaseId)
      const sortedShapes = web_dataStore.sortShapesWithChildren(getAllShapes)
      canvasStore.reloadShape(applyData)
      canvasStore.sortEntities(sortedShapes.map(shape => shape.id.toString()))
      canvasStore.selectShapeById(currentId.toString())
    }
  }
  /**
   * 현재 실제 채우기 상태를 반환하는 함수 (개별 설정 우선, 없으면 전역 설정)
   * @returns {boolean} 채우기 상태
   */
  function getCurrentFillState(): boolean {
    if (!shapeData.value) return false
    const { newShape } = findParentAndReturnShape(shapeData.value)
    const individualSetting = newShape?.shape_data?.isFill
    // 전역 채우기 설정이 없으므로 개별 설정만 확인
    return individualSetting === true
  }
  /**
   * 현재 실제 채우기 상태를 반환하는 함수 (개별 설정 우선, 없으면 전역 설정)
   * @returns {boolean} 채우기 상태
   */
  function getCurrentFillStateOnShape(shape: Defect): boolean {
    const { newShape } = findParentAndReturnShape(shape)
    const individualSetting = newShape?.shape_data?.isFill
    // 전역 채우기 설정이 없으므로 개별 설정만 확인
    return individualSetting === true
  }

  /**
   * 현재 실제 도형 스타일 상태를 반환하는 함수 (개별 설정 우선, 없으면 기본값)
   * @returns {'line' | 'dash' | 'cloud'} 도형 스타일 상태
   */
  function getCurrentShapeState(): 'line' | 'dash' | 'cloud' {
    if (!shapeData.value) return 'line'
    const { newShape } = findParentAndReturnShape(shapeData.value)
    const individualSetting = newShape?.shape_data?.rectType
    // 개별 설정이 있으면 사용, 없으면 기본값 'line' 사용
    return individualSetting || 'line'
  }

  function getCurrentTailStyle(): 'arrow' | 'dot' {
    if (!shapeData.value) return 'dot'
    const { newShape } = findParentAndReturnShape(shapeData.value)
    // POINT는 shape_data.dotType, TRIPOD는 tripodShapePoints.tailStyle 사용
    if (newShape?.shape_type === SHAPES.POINT) {
      return newShape?.shape_data?.dotType === 'arrow' ? 'arrow' : 'dot'
    }
    const individualSetting = newShape?.shape_data?.tripodShapePoints?.tailStyle
    return individualSetting || 'dot'
  }

  function getCurrentShapeStateOnShape(shape: Defect): 'line' | 'dash' | 'cloud' {
    const individualSetting = shape.shape_data?.rectType
    // 개별 설정이 있으면 사용, 없으면 기본값 'line' 사용
    return individualSetting || 'line'
  }

  // balloonFillOpacity 값 가져오기
  function getBalloonFillOpacity(): number {
    const userSettingConfig = allTaskStore.userSettingConfig
    if (!userSettingConfig) return 1

    const styleConfig = userSettingConfig.find(item => item.key === 'style')?.settings
    if (!styleConfig) return 1

    const balloonFillOpacitySetting = styleConfig.find(
      setting => setting.key === 'balloonFillOpacity',
    )
    if (!balloonFillOpacitySetting) return 1

    return balloonFillOpacitySetting.value as number
  }

  function findParentAndReturnShape(shape: Defect): {
    newShape: Defect
    previousShape: Defect
    currentId: string
  } {
    let newShape = cloneDeep(shape) as Defect
    let previousShape = cloneDeep(newShape) as Defect
    let currentId = isApp() ? (newShape as Defect).localStringId : newShape.id.toString()

    if (shape.parent_defect_id) {
      const findParentShape = relatedShapes.value.find(relatedShape => {
        return (
          String(relatedShape.id) == String(newShape.parent_defect_id) ||
          ('localStringId' in relatedShape &&
            relatedShape.localStringId &&
            String(relatedShape.localStringId) == String(newShape.parent_defect_id))
        )
      })
      if (findParentShape) {
        newShape = cloneDeep(findParentShape) as Defect
        previousShape = cloneDeep(newShape) as Defect
        currentId = isApp() ? (newShape as Defect).localStringId : newShape.id.toString()
      }
    }
    return {
      newShape,
      previousShape,
      currentId,
    }
  }

  /**
   * 채우기 기능을 처리하는 함수
   * @returns {Promise<void>}
   */
  async function fillHandler(): Promise<void> {
    if (!shapeData.value) return
    const { newShape, currentId } = findParentAndReturnShape(shapeData.value)
    // 현재 실제 채우기 상태의 반대로 개별 설정 적용
    const currentActualState = getCurrentFillStateOnShape(newShape)
    const newIndividualSetting = !currentActualState
    newShape.shape_data.isFill = newIndividualSetting
    await updateShapeValue(newShape)

    // Canvas Engine에서 개별 설정 적용
    if (currentId) {
      const entity = canvasStore.getEntityById(currentId.toString())
      if (entity) {
        entity.setIndividualFillSetting(newIndividualSetting)
        // 개별 설정 저장 후 채우기 토글 호출
        entity.setShapeFillToggle(false) // 전역 채우기 설정이 없으므로 false 사용
      }
    }
  }

  /**
   * 점 도형의 dotType (dot ↔ arrow) 토글 처리
   * @param targetShape - 토글 대상 결함 데이터
   * @returns {Promise<void>}
   */
  async function togglePointDotTypeForShape(targetShape: Defect): Promise<void> {
    const { newShape, currentId } = findParentAndReturnShape(targetShape)
    const currentDotType: 'dot' | 'arrow' =
      newShape.shape_data?.dotType === 'arrow' ? 'arrow' : 'dot'
    const nextDotType: 'dot' | 'arrow' = currentDotType === 'dot' ? 'arrow' : 'dot'

    const applyData = {
      ...newShape,
      shape_data: {
        ...newShape.shape_data,
        dotType: nextDotType,
        type: SHAPES.POINT,
      },
      shape_type: SHAPES.POINT,
    }

    if (isApp()) {
      const facilityStore = useAppEditorFacilityStore()
      await app_shapeStore.updateShapeToHistory(applyData, true)
      const getAllShape = app_shapeStore.getSortedShapes(facilityStore.selectedFloorDatabaseId)
      canvasStore.reloadShape(applyData)
      canvasStore.sortEntities(getAllShape.map(shape => shape.localStringId.toString()))
      canvasStore.selectShapeById(currentId)
    } else {
      const facilityStore = useWebEditorFacilityStore()
      await web_dataStore.updateShapeToHistory(applyData, true)
      const getAllShapes = web_dataStore.getShapes(facilityStore.selectedFloorDatabaseId)
      const sortedShapes = web_dataStore.sortShapesWithChildren(getAllShapes)
      canvasStore.reloadShape(applyData)
      canvasStore.sortEntities(sortedShapes.map(shape => shape.id.toString()))
      canvasStore.selectShapeById(currentId.toString())
    }

    // 사용자의 마지막 dotType 선택을 localStorage에 저장
    localStorage.setItem('defaultPointDotType', nextDotType)
  }

  /**
   * 다중선택된 POINT/TRIPOD에 대해 그룹 꼬리(화살표 ↔ 점)를 일괄 토글
   * 첫번째 도형의 현재 상태를 기준으로 반대 값으로 모든 도형에 적용
   * @returns {Promise<void>}
   */
  async function handleMultipleTripodTailToggle(): Promise<void> {
    if (currentShapeIds.value.length === 0) return

    // 첫번째 도형의 현재 tail style 기준
    const firstId = currentShapeIds.value[0]
    const firstShape = isApp() ? GetItem.value(firstId) : web_dataStore.getShape(firstId)
    if (!firstShape) return
    let firstTailStyle: 'dot' | 'arrow' = 'dot'
    if (firstShape.shape_type === SHAPES.POINT) {
      firstTailStyle = firstShape.shape_data?.dotType === 'arrow' ? 'arrow' : 'dot'
    } else if (firstShape.shape_type === SHAPES.TRIPOD) {
      firstTailStyle = firstShape.shape_data?.tripodShapePoints?.tailStyle || 'dot'
    }
    const nextTailStyle: 'dot' | 'arrow' = firstTailStyle === 'dot' ? 'arrow' : 'dot'

    // POINT 도형들은 dotType 일괄 변경
    const pointIds = currentShapeIds.value.filter(id => {
      const s = isApp() ? GetItem.value(id) : web_dataStore.getShape(id)
      return s?.shape_type === SHAPES.POINT
    })
    if (pointIds.length > 0) {
      await updateMultipleShapeValue(
        pointIds,
        shape => ({
          shape_data: {
            ...shape.shape_data,
            dotType: nextTailStyle,
          },
        }),
        `${pointIds.length}개 결함 꼬리 변경이`,
      )
      // 캔버스 엔티티 즉시 반영 (각 entity가 reloadShape 없이도 즉시 보이도록)
      pointIds.forEach(id => {
        const entity = canvasStore.getEntityById(id)
        if (entity) {
          entity.setIndividualDotTypeSetting(nextTailStyle)
          entity.setDotStyleToggle(nextTailStyle)
        }
      })
    }

    // TRIPOD 도형들은 tailStyle 일괄 변경
    const tripodIds = currentShapeIds.value.filter(id => {
      const s = isApp() ? GetItem.value(id) : web_dataStore.getShape(id)
      return s?.shape_type === SHAPES.TRIPOD
    })
    if (tripodIds.length > 0) {
      await updateMultipleShapeValue(
        tripodIds,
        shape => {
          const prevTripod = shape.shape_data?.tripodShapePoints
          return {
            shape_data: {
              ...shape.shape_data,
              tripodShapePoints: prevTripod
                ? { ...prevTripod, tailStyle: nextTailStyle }
                : prevTripod,
            },
          }
        },
        `${tripodIds.length}개 그룹 꼬리 변경이`,
      )
      tripodIds.forEach(id => {
        const entity = canvasStore.getEntityById(id)
        if (entity && entity.isTripod()) {
          entity.setTailStyle(nextTailStyle)
        }
      })
    }

    // 마지막 선택을 localStorage에 저장 (POINT가 하나라도 있을 때만)
    if (pointIds.length > 0) {
      localStorage.setItem('defaultPointDotType', nextTailStyle)
    }
  }

  /**
   * 도형 스타일을 처리하는 함수 (line -> dash -> cloud -> line 순환)
   * @returns {Promise<void>}
   */
  async function shapeHandler(): Promise<void> {
    if (!shapeData.value) return
    const { newShape, currentId } = findParentAndReturnShape(shapeData.value)

    // 현재 상태에 따라 다음 상태로 변경
    const currentState = getCurrentShapeStateOnShape(newShape)
    let nextState: 'line' | 'dash' | 'cloud'

    switch (currentState) {
      case 'line':
        nextState = 'dash'
        break
      case 'dash':
        nextState = 'cloud'
        break
      case 'cloud':
        nextState = 'line'
        break
      default:
        nextState = 'line'
        break
    }

    newShape.shape_data.rectType = nextState
    await updateShapeValue(newShape)

    // Canvas Engine에서 개별 설정 적용
    if (currentId) {
      const entity = canvasStore.getEntityById(currentId.toString())
      if (entity) {
        entity.setIndividualShapeTypeSetting(nextState)
        // 개별 설정 저장 후 도형 스타일 적용
        entity.setShapeStyleToggle(nextState)
      }
    }
  }

  // shape 타입에 따라 필요한 버튼 목록 계산
  const buttons = computed(() => {
    let buttonIds: readonly string[] = []
    const shapeType = currentShapeType.value
    if (!shapeType) buttonIds = SHAPE_BUTTON_CONFIG.DEFAULT
    else buttonIds = SHAPE_BUTTON_CONFIG[shapeType as keyof typeof SHAPE_BUTTON_CONFIG]

    return buttonIds.map(buttonId => {
      const buttonDef = BUTTON_DEFINITIONS[buttonId as keyof typeof BUTTON_DEFINITIONS]
      let text = buttonDef.text

      // 채우기 버튼의 경우 현재 상태에 따른 텍스트 표시
      if (buttonId === 'fill') {
        const currentFillState = getCurrentFillState()
        text = (currentFillState ? '채움' : '투명') as ButtonDefType
      }

      // 도형 스타일 버튼의 경우 현재 상태에 따른 다음 상태 텍스트 표시
      if (buttonId === 'shape') {
        const currentShapeState = getCurrentShapeState()
        let nextText: string
        switch (currentShapeState) {
          case 'line':
            nextText = '실선'
            break
          case 'dash':
            nextText = '점선'
            break
          case 'cloud':
            nextText = '구름'
            break
          default:
            nextText = '실선'
            break
        }
        text = nextText as ButtonDefType
      }

      return {
        id: buttonId,
        text: text as ButtonDefType,
      }
    })
  })

  // 번호 이동 옵션 토글
  function toggleOrderOptions(): void {
    const willShow = !showOrderOptions.value
    showOrderOptions.value = willShow
    if (willShow) {
      const buttonElement = document.querySelector('[data-action="order"]') as HTMLElement
      if (buttonElement) {
        updateMenuPosition('order', buttonElement, 80)
      }
    }
  }

  /**
   * 버튼 클릭 핸들러
   * @param {string} buttonId - 클릭된 버튼의 ID
   * @returns {void}
   */
  async function handleButtonClick(buttonId: string): Promise<void> {
    if (!currentShapeId.value && !isMultipleSelection.value) return

    // 다른 메뉴 닫기
    if (buttonId !== 'color') showColorPalette.value = false
    if (buttonId !== 'align') showAlignOptions.value = false
    if (buttonId !== 'delete') showDeleteOptions.value = false
    if (buttonId !== 'order') showOrderOptions.value = false

    // 단일 선택
    if (currentShapeId.value && !isMultipleSelection.value) {
      switch (buttonId) {
        // 복사
        case 'copy': {
          props.onCopy?.(currentShapeId.value)
          break
        }
        // 삭제
        case 'delete': {
          // 자식 있음: 삭제 옵션 표시
          if (hasChildShapes.value) {
            const willShow = !showDeleteOptions.value
            showDeleteOptions.value = willShow

            if (willShow) {
              const buttonElement = document.querySelector('[data-action="delete"]') as HTMLElement
              if (buttonElement) {
                updateMenuPosition('delete', buttonElement, 100) // 삭제 옵션 예상 높이
              }
            }
          }
          // 자식 없음: 바로 삭제
          else props.onDelete?.([currentShapeId.value])
          break
        }
        // 채움 스타일
        case 'fill': {
          if (!userStore.checkEnterprise(true)) return
          fillHandler()
          break
        }
        // 도형 스타일
        case 'shape': {
          if (!userStore.checkEnterprise(true)) return
          shapeHandler()
          break
        }
        // 색상 변경
        case 'color': {
          if (!userStore.checkEnterprise(true)) return
          // 색상 팔레트 토글
          const willShow = !showColorPalette.value
          showColorPalette.value = willShow

          if (willShow) {
            const buttonElement = document.querySelector('[data-action="color"]') as HTMLElement
            if (buttonElement) {
              updateMenuPosition('color', buttonElement, 120) // 색상 팔레트 예상 높이
            }
          }
          break
        }
        // 추가 (결함 attach)
        case 'attach': {
          props.onAttach?.(currentShapeId.value)
          break
        }
        // 트라이포드 결합 해제
        case 'ungroup': {
          if (!currentShapeId.value) return
          props.onUngroup?.(currentShapeId.value)
          break
        }
        case 'tripodTail': {
          if (!userStore.checkEnterprise(true)) return
          if (!currentShapeId.value) return
          // POINT는 dotType 토글, TRIPOD는 기존 콜백
          if (shapeData.value && currentShapeType.value === SHAPES.POINT) {
            await togglePointDotTypeForShape(shapeData.value)
          } else {
            props.onTripodTail?.(currentShapeId.value)
          }
          break
        }
        // 번호 이동
        case 'order': {
          toggleOrderOptions()
          break
        }
        // 단일 선택 현재차로/전차로 변경 (전차등록모드에서는 동작하지 않음)
        case 'becomeCurrent': {
          if (!currentShapeId.value || isPrevRegistMode.value) return
          await handleSingleBecomeCurrent(1)
          break
        }
        case 'becomePrevious': {
          if (!currentShapeId.value || isPrevRegistMode.value) return
          await handleSingleBecomeCurrent(0)
          break
        }
        default:
          return
      }
      return
    }

    // 다중 선택
    if (isMultipleSelection.value) {
      switch (buttonId) {
        // 색상 변경
        case 'color': {
          if (!userStore.checkEnterprise(true)) return
          // 색상 팔레트 토글
          const willShow = !showColorPalette.value
          showColorPalette.value = willShow

          if (willShow) {
            const buttonElement = document.querySelector('[data-action="color"]') as HTMLElement
            if (buttonElement) {
              updateMenuPosition('color', buttonElement, 120) // 색상 팔레트 예상 높이
            }
          }
          break
        }
        // 정렬
        case 'align': {
          const willShow = !showAlignOptions.value
          showAlignOptions.value = willShow

          if (willShow) {
            const buttonElement = document.querySelector('[data-action="align"]') as HTMLElement
            if (buttonElement) {
              updateMenuPosition('align', buttonElement, 80) // 정렬 옵션 예상 높이
            }
          }
          break
        }
        // 삭제
        case 'delete': {
          props.onDelete?.(currentShapeIds.value)
          break
        }
        // 점도형 결합
        case 'combine': {
          props.onCombine?.(currentShapeIds.value)
          break
        }
        // 그룹 꼬리 (POINT/TRIPOD 다중 토글)
        case 'tripodTail': {
          if (!userStore.checkEnterprise(true)) return
          await handleMultipleTripodTailToggle()
          break
        }
        // 번호 이동
        case 'order': {
          toggleOrderOptions()
          break
        }
        // 현재차로 변경 (전차등록모드에서는 동작하지 않음)
        case 'becomeCurrent': {
          if (isPrevRegistMode.value) return
          await handleMultipleBecomeCurrent(1)
          break
        }
        // 전차로 변경
        case 'becomePrevious': {
          if (isPrevRegistMode.value) return
          await handleMultipleBecomeCurrent(0)
          break
        }
        default:
          return
      }
    }
  }

  onUnmounted(() => {
    shapeData.value = null
    showColorPalette.value = false
    showAlignOptions.value = false
    showDeleteOptions.value = false
    showOrderOptions.value = false
  })

  /**
   * Defect 값을 업데이트하는 함수
   * @param {Defect} newShape - 업데이트할 shape 데이터
   * @returns {Promise<void>}
   */
  async function updateShapeValue(newShape: Defect): Promise<void> {
    if (isApp()) {
      const localShape = {
        ...(newShape as Defect),
        modifiedFromLocal: true,
      }
      await app_shapeStore.updateShapeToHistory(
        localShape as Parameters<typeof app_shapeStore.updateShapeToHistory>[0],
      )
    } else {
      await web_dataStore.updateShapeToHistory(newShape as Defect)
    }
  }

  /**
   * 여러 Defect 값을 업데이트하는 함수
   * @param {string[]} shapeIds - 업데이트할 shape ID들
   * @param {Function} updateFunc - 업데이트 함수
   * @param {string} historyMessage - 히스토리 메시지
   * @returns {Promise<void>}
   */
  async function updateMultipleShapeValue<T = Defect>(
    shapeIds: string[],
    updateFunc: (shape: T) => Partial<T>,
    historyMessage: string,
  ): Promise<void> {
    if (isApp()) {
      // 앱 환경에서는 Defect 타입으로 처리
      await app_shapeStore.updateMultipleShapeToHistory(
        shapeIds,
        updateFunc as unknown as Parameters<typeof app_shapeStore.updateMultipleShapeToHistory>[1],
        historyMessage,
      )
    } else {
      // 웹 환경에서는 Defect 타입으로 처리
      await web_dataStore.updateMultipleShapeToHistory(
        shapeIds,
        updateFunc as unknown as (shape: Defect) => Partial<Defect>,
        historyMessage,
      )
    }
  }
  // 단일 선택 시 현재차/전차 변경
  async function handleSingleBecomeCurrent(value: number): Promise<void> {
    const id = currentShapeId.value
    if (!id) return
    if (isApp()) {
      await app_shapeStore.setIsBecomeCurrent(id, value)
    } else {
      await web_dataStore.setIsBecomeCurrent(id, value)
    }
    const label = value ? '현재차로' : '전차로'
    toast.add('success', `결함이 ${label} 변경되었습니다.`)
  }

  // 다중 선택 시 현재차/전차 일괄 변경
  async function handleMultipleBecomeCurrent(value: number): Promise<void> {
    const undoRedo = useUndoRedo()
    const targetIds = currentShapeIds.value.filter(id => {
      const shape = isApp() ? GetItem.value(id) : web_dataStore.getShape(id)
      return shape && isPreviousShapeUtil(shape.previous_defect_id)
    })
    const label = value ? '현재차 전환이' : '전차 전환이'
    undoRedo.startBatch()
    for (const id of targetIds) {
      if (isApp()) {
        await app_shapeStore.setIsBecomeCurrent(id, value)
      } else {
        await web_dataStore.setIsBecomeCurrent(id, value)
      }
    }
    undoRedo.endBatch(`${targetIds.length}개 ${label}`)
    toast.add(
      'success',
      `${targetIds.length}개 결함이 ${value ? '현재차로' : '전차로'} 변경되었습니다.`,
    )
  }

  // 다중 선택 정렬의 추가 옵션 중 꺾은선 정렬 선택지를 노출할지 판단하는 함수
  function checkShowDirectionAlignOptions(): boolean {
    // 방향 도형갯수 확인
    const directions = currentShapeIds.value.filter(
      shapeId => web_dataStore.getShape(shapeId)?.shape_type === SHAPES.DIRECTION,
    )
    return directions.length > 1
  }

  /**
   * 그랩된 도형들 정렬 핸들러
   * @param align 정렬 방향 (left/right/top/bottom/horizontal/vertical)
   * @param type 정렬 대상 (balloon: 번호풍선, direction: 꺾은선)
   */
  async function handleGrabAlign(
    align: 'horizontal' | 'left' | 'right' | 'bottom' | 'vertical' | 'top',
    type: 'balloon' | 'direction',
  ): Promise<void> {
    try {
      const canvas = canvasStore.getCanvasEngine()
      if (!canvas) throw new Error('Not Found Canvas.')
      if (!grabBoxRect.value) throw new Error('Not Found GrabBox.')

      // 번호 풍선 정렬
      if (type === 'balloon') {
        // 유효한 벌룬 데이터 수집
        const balloonData: BalloonData[] = currentShapeIds.value
          .map(id => canvas.getEntityById(id))
          .filter(
            (shape?: CanvasEngineEntity | null): shape is CanvasEngineEntity =>
              !!shape && !shape.isBrush(), // 브러시는 제외
          )
          .map(shape => {
            const pos = shape.getCurrentBalloonGlobalPosition()
            const centerPoint = shape.getCenterPoint()
            const headBox = shape.getBalloonHeadBox()
            // 벌룬 크기 정보 수집
            const [w, h, stroke] = [
              headBox?.width || 30,
              headBox?.height || 30,
              (headBox?.strokeWidth || 2) / 2,
            ]
            return {
              shape,
              pos,
              width: w,
              height: h,
              stroke,
              centerPoint,
            }
          })

        if (balloonData.length === 0) {
          toast.add('warning', '정렬할 벌룬이 없습니다.')
          return
        }

        // 정렬 기준값 계산 (가장 왼쪽/오른쪽/위/아래 또는 중앙)
        const targetValue = calculateAlignTargetValue(balloonData, align as AlignDirection)

        // 벌룬들을 정렬하여 최종 위치 계산
        // - 현재 위치 기준으로 정렬 순서 결정
        // - 가까운 벌룬들(100px 이내)은 딱 붙이고, 먼 벌룬들은 원래 간격 유지
        const arrangedBalloons = alignBalloonsInOrder(
          balloonData,
          targetValue,
          align as AlignDirection,
        )

        // 각 벌룬을 계산된 위치로 이동
        let hasChanged = false
        for (const { shape, finalPos } of arrangedBalloons) {
          const originalData = balloonData.find(b => b.shape === shape)
          if (!originalData) continue

          // 위치 변경 여부 확인 (0.5px 이하는 무시)
          const deltaX = Math.abs(finalPos.x - originalData.pos.x)
          const deltaY = Math.abs(finalPos.y - originalData.pos.y)

          if (deltaX > 0.5 || deltaY > 0.5) {
            if (shape.isTripod()) {
              // 그룹도형(삼발이)은 이동하지 않음
              continue
            } else if (shape.isDirection()) {
              // 방향도형: 벌룬 위치까지의 최적 경로 재계산
              const points = shape.getPoints()
              // 유연한 모드로 경로 탐색 (각도 허용 오차 증가, 탐색 범위 확대)
              let path = findDirectionPathToBalloon(points, finalPos, true)

              // 경로 탐색 실패 시 폴백: 끝점만 이동
              if (!path) {
                const newPoints = [...points]
                newPoints[newPoints.length - 2] = finalPos.x
                newPoints[newPoints.length - 1] = finalPos.y
                path = newPoints
              }

              if (path) {
                shape.updateDirectionPath(path)
                hasChanged = true
              }
            } else {
              // 일반 도형(점, 영역): 벌룬만 이동
              shape.moveBalloonToGlobalPosition(finalPos)
              hasChanged = true
            }
          }
        }

        // 변경사항 저장 및 화면 갱신
        if (hasChanged) {
          canvas.onTransformShapes(currentShapeIds.value)
          canvas.syncHighlights(currentShapeIds.value)
        }
        return
      }

      // 꺾은선 정렬
      if (type === 'direction') {
        const directionShapes = currentShapeIds.value
          .map(id => canvas.getEntityById(id))
          .filter((shape): shape is CanvasEngineEntity => Boolean(shape) && !!shape?.isDirection())

        if (directionShapes.length === 0) {
          toast.add('warning', '정렬할 방향 도형이 없습니다.')
          return
        }

        if (directionShapes.length < 2) {
          toast.add('warning', '2개 이상의 방향 도형을 선택해주세요.')
          return
        }

        const alignResults = alignDirectionShapes(directionShapes, align as AlignDirection)

        if (alignResults.length > 0) {
          // 변경사항 적용
          alignResults.forEach(result => {
            result.shape.updateDirectionPath(result.newPoints)
          })

          canvas.onTransformShapes(currentShapeIds.value)
          canvas.syncHighlights(currentShapeIds.value)
          toast.add('success', `${alignResults.length}개 방향 도형이 정렬되었습니다.`)
        } else {
          toast.add('info', '정렬할 방향 도형이 없습니다.')
        }
        return
      }
    } catch (error) {
      console.error(error)
      toast.add('error', '정렬 중 오류가 발생했습니다.')
    }
  }

  // 현재 층의 부모 shape 중 번호 이동 가능한 항목만 정렬하여 반환
  function getOrderableParentShapes(): Defect[] {
    if (isApp()) {
      const facilityStore = useAppEditorFacilityStore()
      const floorId = facilityStore.selectedFloorDatabaseId
      if (!floorId) return []
      const allShapes = app_shapeStore.currentFloorBranchShapes(floorId)
      return sortByKeys(allShapes.filter(s => !s.parent_defect_id && checkIsPrevious(s)))
    } else {
      const facilityStore = useWebEditorFacilityStore()
      const floorId = facilityStore.selectedFloorDatabaseId
      if (!floorId) return []
      const allShapes = web_dataStore.getShapes(Number(floorId))
      return sortByKeys(allShapes.filter(s => !s.parent_defect_id && checkIsPrevious(s)))
    }
  }

  // shape ID 헬퍼
  function getShapeStoreId(shape: Defect): string {
    return isApp()
      ? (shape as Defect & { localStringId: string }).localStringId
      : shape.id.toString()
  }

  // sort_key 데이터 저장 + undo/redo 등록 (캔버스 갱신은 별도 호출)
  async function saveSortKey(shapeId: string, newSortKey: string): Promise<void> {
    if (isApp()) {
      await app_shapeStore.updateSortKeyToHistory(shapeId, newSortKey)
    } else {
      const beforeShape = web_dataStore.getShape(shapeId)
      if (!beforeShape) return
      const beforeSortKey = beforeShape.sort_key
      await web_dataStore.updateShape({ id: beforeShape.id, sort_key: newSortKey })

      const undoRedo = useUndoRedo()
      undoRedo.execute(
        '번호 변경이',
        async () => {
          await web_dataStore.updateShape({ id: beforeShape.id, sort_key: beforeSortKey })
          refreshCanvasOrder()
        },
        async () => {
          await web_dataStore.updateShape({ id: beforeShape.id, sort_key: newSortKey })
          refreshCanvasOrder()
        },
      )
    }
  }

  // 캔버스 캐시 클리어 + 엔티티 정렬 + 풍선 번호 갱신
  function refreshCanvasOrder(): void {
    // 캐시 클리어하여 batchDraw 시 실제 노드(갱신된 풍선 번호)로 렌더되도록 함
    const engine = canvasStore.getCanvasEngine()
    if (engine) engine.clearAllEntityCache()
    if (isApp()) {
      app_shapeStore.updateShapes()
    } else {
      web_dataStore.updateShapes()
    }
    // 사이드바 풍선 번호 리렌더 트리거
    canvasStore.bumpIndexVersion()
  }

  /**
   * 번호 이동 핸들러
   * @param direction - 'up' | 'down'
   */
  async function handleOrderChange(direction: 'up' | 'down'): Promise<void> {
    showOrderOptions.value = false

    const parentShapes = getOrderableParentShapes()
    if (parentShapes.length < 2) return

    // 단일 선택
    if (currentShapeId.value && !isMultipleSelection.value) {
      if (!shapeData.value) return
      const { newShape } = findParentAndReturnShape(shapeData.value)
      const targetId = getShapeStoreId(newShape)

      const currentIndex = parentShapes.findIndex(s => getShapeStoreId(s) === targetId)
      if (currentIndex === -1) return

      // 경계 검사
      if (direction === 'up' && currentIndex === 0) return
      if (direction === 'down' && currentIndex === parentShapes.length - 1) return

      let newSortKey: string
      if (direction === 'up') {
        const prevPrev = currentIndex >= 2 ? parentShapes[currentIndex - 2].sort_key : null
        const prev = parentShapes[currentIndex - 1].sort_key
        newSortKey = generateKeyBetween(prevPrev, prev)
      } else {
        const next = parentShapes[currentIndex + 1].sort_key
        const nextNext =
          currentIndex + 2 < parentShapes.length ? parentShapes[currentIndex + 2].sort_key : null
        newSortKey = generateKeyBetween(next, nextNext)
      }

      await saveSortKey(targetId, newSortKey)
      refreshCanvasOrder()
      return
    }

    // 다중 선택 (grab)
    if (isMultipleSelection.value) {
      // 선택된 shape들의 부모 ID 수집
      const selectedParentIds = new Set<string>()
      for (const id of currentShapeIds.value) {
        const shape = isApp() ? app_shapeStore.GetItem(id) : web_dataStore.getShape(id)
        if (!shape) continue
        if (shape.parent_defect_id) {
          selectedParentIds.add(shape.parent_defect_id.toString())
        } else {
          selectedParentIds.add(getShapeStoreId(shape))
        }
      }

      // parentShapes 중에서 선택된 것과 미선택 인덱스 구분
      const selectedIndices: number[] = []
      const unselectedIndices: number[] = []
      parentShapes.forEach((s, i) => {
        if (selectedParentIds.has(getShapeStoreId(s))) {
          selectedIndices.push(i)
        } else {
          unselectedIndices.push(i)
        }
      })

      if (selectedIndices.length === 0) return

      const minSelectedIdx = Math.min(...selectedIndices)
      const maxSelectedIdx = Math.max(...selectedIndices)

      // 미선택 shape 1개를 찾아 이동 (그룹 외부 우선, 없으면 그룹 내부)
      if (direction === 'up') {
        // 선택 그룹 위의 미선택 shape를 그룹 아래로 이동
        let candidates = unselectedIndices.filter(i => i < minSelectedIdx)
        // 외부에 없으면 그룹 내부 미선택 shape (maxSelectedIdx에 가장 가까운 것)
        if (candidates.length === 0) {
          candidates = unselectedIndices.filter(i => i > minSelectedIdx && i < maxSelectedIdx)
        }
        if (candidates.length === 0) return
        const targetIdx = Math.max(...candidates)
        const target = parentShapes[targetIdx]

        // target을 maxSelectedIdx 뒤로 이동
        const afterKey = parentShapes[maxSelectedIdx].sort_key
        const nextIdx = maxSelectedIdx + 1
        const beforeKey = nextIdx < parentShapes.length ? parentShapes[nextIdx].sort_key : null
        const newSortKey = generateKeyBetween(afterKey, beforeKey)

        await saveSortKey(getShapeStoreId(target), newSortKey)
      } else {
        // 선택 그룹 아래의 미선택 shape를 그룹 위로 이동
        let candidates = unselectedIndices.filter(i => i > maxSelectedIdx)
        // 외부에 없으면 그룹 내부 미선택 shape (minSelectedIdx에 가장 가까운 것)
        if (candidates.length === 0) {
          candidates = unselectedIndices.filter(i => i > minSelectedIdx && i < maxSelectedIdx)
        }
        if (candidates.length === 0) return
        const targetIdx = Math.min(...candidates)
        const target = parentShapes[targetIdx]

        // target을 minSelectedIdx 앞으로 이동
        const afterKey = parentShapes[minSelectedIdx].sort_key
        const prevIdx = minSelectedIdx - 1
        const beforeKey = prevIdx >= 0 ? parentShapes[prevIdx].sort_key : null
        const newSortKey = generateKeyBetween(beforeKey, afterKey)

        await saveSortKey(getShapeStoreId(target), newSortKey)
      }

      refreshCanvasOrder()
    }
  }

  // 전차인지 판단하는 함수 (progressing에 의존하지 않음)
  function checkIsPrevious(shape: Defect | null | undefined): boolean {
    if (!shape) return false

    // 전차 항목이면서 현재차로 변경된 경우 → 편집 가능
    if (isPreviousShapeUtil(shape.previous_defect_id) && shape.is_current) {
      return true
    }
    // 전차 항목(현재차 미전환)인 경우 → 사진 보유 여부로만 판단
    if (isPreviousShapeUtil(shape.previous_defect_id) && !shape.is_current) {
      const shapeId = isApp() ? (shape as Defect).localStringId : shape.id.toString()
      const isPreviousHasDefectPhoto = isApp()
        ? app_defectPhotoStore.getIsPreviousHasDefectPhoto(shapeId)
        : web_dataStore.getIsPreviousHasDefectPhoto(shapeId)
      return isPreviousHasDefectPhoto
    }
    // 현차 결함
    return true
  }
</script>

<style scoped lang="scss">
  .action-button-group {
    display: flex;
    flex-direction: row;
    gap: 8px;
    background: rgba(0, 0, 0, 0.8);
    border-radius: 0px 8px 8px 8px;
    padding: 4px 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    position: relative;

    .minimize-toggle {
      position: absolute;
      bottom: 100%;
      left: 0;
      width: 40px;
      height: 22px;
      background: rgba(0, 0, 0, 0.8);
      border: none;
      border-radius: 8px 8px 0 0;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      padding: 0;
      color: #fff;

      &:hover {
        background: var(--Primary);
      }
    }

    &.is-minimized {
      background: transparent;
      box-shadow: none;
      padding: 0;
      gap: 0;

      .minimize-toggle {
        position: static;
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.8);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        border: none;
      }
    }

    .shape-type-button {
      min-width: 58px;
      height: 52px;
      padding: 0 4px 0 0;
      color: var(--White);
      font-size: 12px;
      font-weight: 400;
      display: flex;
      position: relative;
      align-items: center;
      justify-content: center;
      &::after {
        content: '';
        display: block;
        width: 1px;
        height: 70%;
        background: var(--White);
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      }
      .sub-button-group {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        background: rgba(0, 0, 0, 0.8);
        border-radius: 8px;
        padding: 10px;
        display: flex;
        flex-direction: column;
        gap: 8px;

        &.position-top {
          top: auto;
          bottom: calc(100% + 8px);
        }
        .sub-button {
          width: 100%;
          min-width: 51px;
          height: 22px;
          border-radius: 4px;
          background: transparent;
          color: var(--White);
          font-size: 12px;
          font-weight: 400;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 4px;
          &.can-hover {
            &:hover {
              background: var(--Primary);
            }
          }
          &:active {
            background: var(--Primary);
          }
          .anchor-point {
            width: 20px;
            display: flex;
            padding-left: 4px;
            height: 16px;
            align-items: center;
            justify-content: center;
          }
          p {
            margin: 0;
            flex: 1;
            text-align: left;
          }
        }
      }
    }

    &.no-hover {
      .action-button {
        &:hover {
          background: transparent;
          color: var(--White);
        }
      }

      .color-option {
        &:hover {
          border-color: var(--White);
          transform: scale(1);
        }
      }

      .align-option {
        &:not(.disabled):hover {
          border-color: transparent;
          transform: none;
        }
      }

      .delete-option {
        &:hover {
          background: transparent;
          color: var(--White);
        }
      }
    }

    // brush인 경우 삭제 버튼만 표시
    &.is-brush {
      .action-button:not([data-action='delete']) {
        display: none;
      }
    }

    .action-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 53px;
      height: 52px;
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      border-radius: 8px;
      padding: 4px;
      gap: 5px;
      position: relative;

      &:hover {
        background: var(--Primary);
        color: var(--White);
      }

      &.disabled {
        color: var(--Gray-400);
        cursor: not-allowed;
      }

      .button-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
      }

      .button-text {
        font-size: 12px;
        font-weight: 400;
        text-align: center;
        line-height: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      // 색상 팔레트
      .color-palette {
        position: absolute;
        top: calc(100% + 8px);
        left: -4px;
        background: rgba(0, 0, 0, 0.8);
        border-radius: 8px;
        padding: 12px 20px;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        z-index: 1000;
        backdrop-filter: blur(10px);

        &.position-top {
          top: auto;
          bottom: calc(100% + 8px);
        }

        .color-options {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-direction: row;
          gap: 20px;
          &.outline-options {
            margin-top: 10px;
          }
          & > p {
            font-size: 12px;
            font-weight: 400;
            width: 30px;
            text-align: left;
            color: var(--White);
            word-break: keep-all;
          }

          .color-option {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            cursor: pointer;
            outline: none;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;

            &.active {
              box-shadow: 0 0 0 2px var(--Gray-400);
              svg {
                width: 10px;
                height: 10px;
              }
            }

            &:hover {
              border-color: rgba(255, 255, 255, 0.8);
              transform: scale(1.1);
            }

            &:active {
              transform: scale(0.95);
            }
          }
          .align-option {
            width: 18px;
            height: 18px;
            cursor: pointer;
            outline: none;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;

            &.active {
              svg {
                width: 10px;
                height: 10px;
              }
            }

            &.disabled {
              cursor: default;

              &:hover {
                border-color: transparent;
                transform: none;
              }

              &:active {
                transform: none;
              }
            }

            &:not(.disabled):hover {
              border-color: rgba(255, 255, 255, 0.8);
              transform: scale(1.1);
            }

            &:not(.disabled):active {
              transform: scale(0.95);
            }
          }
        }
      }

      // 삭제 옵션
      .delete-options {
        position: absolute;
        top: calc(100% + 8px);
        left: -4px;
        background: rgba(0, 0, 0, 0.8);
        border-radius: 8px;
        padding: 8px;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        z-index: 1000;
        backdrop-filter: blur(10px);
        min-width: 100px;

        &.position-top {
          top: auto;
          bottom: calc(100% + 8px);
        }

        .delete-option-list {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .delete-option {
            padding: 8px 12px;
            background: transparent;
            color: white;
            cursor: pointer;
            font-size: 0.874rem;
            border-radius: 4px;
            white-space: nowrap;
            text-align: left;
            &:hover {
              background-color: var(--Primary-500);
              color: var(--White);
            }
          }
        }
      }
    }
  }
</style>
